import { base64Kit } from '@/utils/base64-kit';
import { isHttpUri } from '@/utils/file-kit';

interface ScreenshotFingerprintOptions {
    fingerprintData: unknown;
}

interface GetOrCreateScreenshotUrlCodeOptions extends ScreenshotFingerprintOptions {
    createDataUrl: () => Promise<string | null | undefined> | string | null | undefined;
}

interface HydrateScreenshotUrlCodeOptions extends ScreenshotFingerprintOptions {
    remoteUrl: string | null | undefined;
}

const pendingCodeMap = new Map<string, Promise<string>>();

export const stringifyScreenshotFingerprint = (fingerprintData: unknown): string => {
    const fingerprintSource = JSON.stringify(fingerprintData, (_key, value) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return value;
        }

        return Object.keys(value)
            .sort()
            .reduce<Record<string, unknown>>((result, key) => {
                result[key] = value[key];
                return result;
            }, {});
    });

    if (!fingerprintSource) {
        throw new Error('screenshot fingerprint data must be JSON serializable');
    }

    return fingerprintSource;
};

export const getScreenshotFingerprintCode = (fingerprintData: unknown) =>
    base64Kit.md5(stringifyScreenshotFingerprint(fingerprintData));

export const hydrateScreenshotUrlCode = ({ fingerprintData, remoteUrl }: HydrateScreenshotUrlCodeOptions) => {
    if (!remoteUrl || !isHttpUri(remoteUrl)) {
        return remoteUrl || '';
    }

    const code = getScreenshotFingerprintCode(fingerprintData);
    base64Kit.setRemoteUrl(code, remoteUrl);
    return code;
};

export const getOrCreateScreenshotUrlCode = async ({
    fingerprintData,
    createDataUrl,
}: GetOrCreateScreenshotUrlCodeOptions) => {
    const fingerprintSource = stringifyScreenshotFingerprint(fingerprintData);
    const code = base64Kit.md5(fingerprintSource);

    if (base64Kit.hasCode(code)) {
        return code;
    }

    const pendingCode = pendingCodeMap.get(code);
    if (pendingCode) {
        return pendingCode;
    }

    const promise = (async () => {
        const dataUrl = await createDataUrl();
        if (!dataUrl) {
            return '';
        }
        return base64Kit.getCode(dataUrl, fingerprintSource);
    })();
    pendingCodeMap.set(code, promise);

    try {
        return await promise;
    } finally {
        if (pendingCodeMap.get(code) === promise) {
            pendingCodeMap.delete(code);
        }
    }
};
